<template>
    <div class="ask-pendo-config-drawer">
        <pendo-button
            v-pendo-tooltip="{
                content: $t('askPendo.Personalization'),
                placement: 'top',
            }"
            icon="settings"
            type="secondary"
            size="mini"
            @click="toggleDrawer(!isConfigDrawerOpen)" />

        <pendo-drawer
            :visible="isConfigDrawerOpen && initialRenderComplete"
            size="350px"
            push=".pendo-page"
            @close="toggleDrawer(false)">
            <template #header>
                <div class="ask-pendo-config-drawer__header">
                    <div class="ask-pendo-config-drawer__header-title">
                        <pendo-icon
                            type="settings"
                            size="16" />
                        {{ $t('askPendo.Personalization') }}
                    </div>
                    <pendo-drawer-close />
                </div>
            </template>
            <template #body>
                <div class="ask-pendo-config-drawer__body">
                    <template v-if="hasCustomInstructionsFlag">
                        <pendo-multiselect
                            :value="currentRole"
                            :options="roleOptions"
                            :loading="savingRole"
                            :disabled="savingRole"
                            label-key="label"
                            value-key="key"
                            @select="onRoleSelect">
                            <template #topLabel>
                                {{ $t('askPendo.Role') }}
                            </template>
                        </pendo-multiselect>
                        <div class="custom-instructions-container">
                            <div class="pendo-input__label pendo-input__label--top">
                                {{ $t('askPendo.Custom instructions') }}
                            </div>
                            <p class="custom-instructions-description">
                                {{ $t('askPendo.custom_instructions_description') }}
                            </p>
                            <pendo-input
                                class="custom-instructions-input"
                                :value="customInstructions"
                                type="textarea"
                                :rows="8"
                                :debounce="300"
                                :placeholder="$t('askPendo.custom_instructions_placeholder')"
                                @input="onCustomInstructionsInput" />
                            <pendo-button
                                :loading="savingCustomInstructions"
                                :disabled="!hasUnsavedChanges"
                                label="Save"
                                type="primary"
                                @click="saveCustomRules" />
                        </div>
                    </template>
                    <template v-if="isPendozer">
                        <pendo-divider>
                            <template #middle>
                                <span class="pendozer-only-text">{{ $t('askPendo.pendozer_only') }}</span>
                            </template>
                        </pendo-divider>
                        <pendo-multiselect
                            :value="model"
                            :options="aiModels"
                            label-key="label"
                            value-key="value"
                            @select="onModelSelect">
                            <template #topLabel>
                                {{ $t('askPendo.AI Model') }}
                            </template>
                        </pendo-multiselect>
                        <pendo-multiselect
                            :value="mode"
                            :options="modes"
                            label-key="label"
                            value-key="value"
                            @select="onModeSelect">
                            <template #topLabel>
                                {{ $t('askPendo.Mode') }}
                            </template>
                        </pendo-multiselect>
                        <pendo-input
                            :value="systemPrompt"
                            type="textarea"
                            :rows="8"
                            :debounce="300"
                            @input="onSystemPromptInput">
                            <template #topLabel>
                                {{ $t('askPendo.System prompt') }}
                            </template>
                        </pendo-input>
                    </template>
                </div>
            </template>
        </pendo-drawer>
    </div>
</template>

<script>
import { PendoButton, PendoDivider, PendoDrawer, PendoDrawerClose, PendoInput, PendoIcon, PendoMultiselect, PendoNotification, PendoTooltip } from '@pendo/components';
import { mapActions, mapGetters, mapState } from 'vuex';
import { PerSubscriptionUserStorage } from '@/utils/storage';
import { JOB_ROLES } from '@/constants/user-job-roles';

const CUSTOM_INSTRUCTIONS_STORAGE_KEY = 'askPendoCustomInstructions';

export default {
    name: 'AskPendoConfigDrawer',
    components: {
        PendoButton,
        PendoDivider,
        PendoDrawer,
        PendoDrawerClose,
        PendoInput,
        PendoIcon,
        PendoMultiselect,
    },
    directives: {
        PendoNotification,
        PendoTooltip,
    },
    data () {
        return {
            initialRenderComplete: false,
            model: {},
            aiModels: [],
            mode: '',
            modes: [],
            systemPrompt: '',
            customInstructions: '',
            savedCustomInstructions: '',
            savingCustomInstructions: false,
            savingRole: false,
        };
    },
    computed: {
        ...mapGetters('askPendo', ['getEmployeeConfig', 'isConfigDrawerOpen']),
        ...mapGetters('auth', ['hasSegmentFlag', 'isPendozer']),
        ...mapState('auth', {
            user: (state) => state.user,
        }),
        hasCustomInstructionsFlag () {
            return this.hasSegmentFlag('customInstructions');
        },
        roleOptions () {
            return Object.values(JOB_ROLES).map((role) => ({ id: role.key, ...role }));
        },
        currentRole () {
            if (!this.user?.jobRole) {
                return null;
            }

            return this.roleOptions.find((role) => role.key === this.user.jobRole) || null;
        },
        hasUnsavedChanges () {
            return this.customInstructions !== this.savedCustomInstructions;
        },
    },
    mounted () {
        // Delay showing the drawer to ensure DOM is ready for push animation when on individual conversation page
        if (this.isConfigDrawerOpen) {
            setTimeout(() => {
                this.initialRenderComplete = true;
            }, 300);
        } else {
            this.initialRenderComplete = true;
        }
    },
    created () {
        // Initialize options with i18n strings
        this.aiModels = [
            {
                label: this.$t('askPendo.configDrawer.Default'),
                value: '',
            },
            {
                label: this.$t('askPendo.configDrawer.OpenAI o3'),
                value: 'o3-2025-04-16',
            },
            {
                label: this.$t('askPendo.configDrawer.ChatGPT 4o'),
                value: 'gpt-4o',
            },
            {
                label: this.$t('askPendo.configDrawer.Gemini 2.5 Flash'),
                value: 'gemini-2.5-flash',
            },
            {
                label: this.$t('askPendo.configDrawer.Gemini 2.5 Pro'),
                value: 'gemini-2.5-pro',
            },
        ];

        this.modes = [
            {
                label: this.$t('askPendo.configDrawer.MCP only'),
                value: 'pendo_mcp',
            },
            {
                label: this.$t('askPendo.configDrawer.Reports only'),
                value: 'artifacts',
            },
            {
                label: this.$t('askPendo.configDrawer.MCP + Reports'),
                value: 'all',
            },
        ];

        this.model = this.aiModels.find((model) => model.value === (this.getEmployeeConfig?.model || '')); // default to default option if employee config is not set
        this.mode = this.modes.find((mode) => mode.value === (this.getEmployeeConfig?.toolSet || 'pendo_mcp')); // default to pendo_mcp if employee config is not set

        this.systemPrompt = this.getEmployeeConfig?.systemPrompt || '';

        this.loadCustomInstructions();
    },
    methods: {
        ...mapActions('askPendo', ['setEmployeeConfigProperty', 'updateConfigDrawerState', 'updateCustomInstructions']),
        ...mapActions('auth', ['updateUserProperties']),
        toggleDrawer (isOpen) {
            this.updateConfigDrawerState({ isOpen });
        },
        onModelSelect ($event) {
            this.model = $event;
            this.setEmployeeConfigProperty({ key: 'model', value: $event.value });
        },
        onModeSelect ($event) {
            this.mode = $event;
            this.setEmployeeConfigProperty({ key: 'toolSet', value: $event.value });
        },
        onSystemPromptInput ($event) {
            this.systemPrompt = $event;
            this.setEmployeeConfigProperty({ key: 'systemPrompt', value: $event });
        },
        async loadCustomInstructions () {
            if (!this.hasCustomInstructionsFlag) return;
            try {
                const setting = await PerSubscriptionUserStorage.get(CUSTOM_INSTRUCTIONS_STORAGE_KEY);
                if (setting?.value) {
                    this.customInstructions = typeof setting.value === 'string' ? setting.value : (setting.value.customRules || '');
                }
                this.savedCustomInstructions = this.customInstructions;
                this.updateCustomInstructions(this.customInstructions);
            } catch (err) {
                console.error('Failed to load custom instructions:', err);
            }
        },
        onCustomInstructionsInput ($event) {
            this.customInstructions = $event;
        },
        async onRoleSelect ($event) {
            if (!$event?.key) return;

            this.savingRole = true;
            try {
                await this.updateUserProperties({ jobRole: $event.key });
                PendoNotification({
                    type: 'success',
                    title: this.$t('askPendo.role_saved_title'),
                    message: this.$t('askPendo.role_saved_message'),
                    duration: 3000,
                });
            } catch (err) {
                PendoNotification({
                    type: 'error',
                    title: this.$t('askPendo.role_save_error_title'),
                    message: this.$t('askPendo.role_save_error_message'),
                    duration: 3000,
                });
                console.error('Failed to save role:', err);
            } finally {
                this.savingRole = false;
            }
        },
        async saveCustomRules () {
            this.savingCustomInstructions = true;
            this.updateCustomInstructions(this.customInstructions);
            try {
                await PerSubscriptionUserStorage.set(CUSTOM_INSTRUCTIONS_STORAGE_KEY, this.customInstructions);
                this.savedCustomInstructions = this.customInstructions;
                PendoNotification({
                    type: 'success',
                    title: this.$t('askPendo.custom_instructions_saved_title'),
                    message: this.$t('askPendo.custom_instructions_saved_message'),
                    duration: 3000,
                });
            } catch (err) {
                PendoNotification({
                    type: 'error',
                    title: this.$t('askPendo.custom_instructions_save_error_title'),
                    message: this.$t('askPendo.custom_instructions_save_error_message'),
                    duration: 3000,
                });
                console.error('Failed to save custom instructions:', err);
            } finally {
                this.savingCustomInstructions = false;
            }
        },
    },
};
</script>

<style scoped lang="less">
.ask-pendo-config-drawer {
    &__header {
        display: flex;
        align-items: center;
        justify-content: space-between;

        &-title {
            display: flex;
            align-items: center;
            gap: 8px;
            font-weight: 600;
            font-size: 14.222px;
        }
    }

    &__body {
        display: flex;
        flex-direction: column;
        gap: 24px;
        padding: 32px 24px;

        .pendozer-only-text {
            white-space: nowrap;
        }
    }
}
:deep(.custom-instructions-input) {
    margin-bottom: 16px;
}
:deep(.pendo-drawer__header) {
    border-bottom: 1px solid var(--palette-gray-40);
}

:deep(.pendo-drawer__content) {
    grid-template-rows: [header] 58px [body] 1fr;
}
</style>
